import React, { FC } from 'react';
import { FormControl, InputLabel, TextField, FormHelperText } from '@mui/material';
import InfoTooltip from '../InfoTooltip/InfoTooltip';

export interface ITextFieldInputProps {
  label: string;
  handleChange: (value: string) => void;
  value?: string | number | undefined;
  hasError?: boolean;
  errorText?: string;
  helperText?: string;
  required?: boolean;
  placeholderText?: string;
  inputType?: string;
  inputProps?: Record<string, string>;
  isDisabled?: boolean;
  rows?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  tooltipText?: string;
}

/**
 * Represents a TextField where users can input Text or Numbers.
 *
 * @param props - The properties for TextFieldInput
 * @param props.handleChange - Callback function that will be used when value changes.
 * @param props.value - Value that controls the component.
 * @param props.hasError - Boolean value to display error
 * @param props.errorText - Error message when the TextField has an error.
 * @param props.helperText - Message to give user context of what the TextInput is used for.
 * @param props.placeholderText - Message when TextInput is empty.
 * @param props.inputType - The data type the TextField accepts.
 * @param props.endAdornment - Icon that is shown at the end of the text field. For example, a Percentage symbol.
 * @param props.inputProps - Optional Options for Input from MaterialUI
 * @param props.isDisabled - Boolean value to disable/enable TextInput
 * @param param0
 * @returns
 */

const TextFieldInput: FC<ITextFieldInputProps> = ({
  label,
  handleChange,
  value,
  helperText,
  hasError,
  errorText,
  placeholderText,
  inputType,
  inputProps,
  isDisabled,
  rows = 5,
  onKeyDown,
  tooltipText,
}) => {
  return (
    <div>
      <FormControl error={hasError} sx={{ my: 1, width: `100%` }}>
        <div className="flex items-center">
          <InputLabel className="pl-1 pr-2 bg-white" shrink={true} htmlFor={label}>
            {label}
          </InputLabel>
          {tooltipText && <InfoTooltip text={tooltipText} placement="right" />}
        </div>
        <TextField
          className={`${hasError ? 'border-error-red' : ''}`}
          id={label}
          value={value}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholderText}
          type={inputType}
          inputProps={inputProps}
          role="textfield"
          disabled={isDisabled}
          multiline={true}
          rows={rows}
          onKeyDown={onKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: hasError ? 'var(--error-red)' : undefined,
              },
              '&.Mui-focused fieldset': {
                borderColor: hasError ? 'var(--error-red)' : undefined,
              },
            },
          }}
        />
        {hasError && errorText && <FormHelperText>{errorText}</FormHelperText>}
        {helperText && !hasError && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </div>
  );
};

export default TextFieldInput;
